import { FC } from 'react';
import { Button, Divider, Tabs, Typography } from 'antd';
import { LucideBarChartBig, LucideShieldCheck } from 'lucide-react';
import { TbArrowUpRight, TbBrush, TbCalendarMonth, TbLockAccess } from 'react-icons/tb';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FlexContentRegion, FullHeightFlexColumn, PlatformAlert } from '@/shared';

import Metadata from './meta';
import { Access, Appearance, Identity, Usage } from './views';

const Advanced: FC = (): JSX.Element => {
    const navigate = useNavigate();
    const url = '/settings/workspace/advanced';
    const { token } = useAntDesignTheme();

    const onChangeTab = (newActiveKey: string) => {
        navigate(`${url}/${newActiveKey}`);
    };

    const items = [
        {
            label: (
                <>
                    <TbBrush
                        size='1rem'
                        style={{
                            marginRight: '0.375rem',
                            verticalAlign: '-0.175rem',
                        }}
                    />
                    Appearance
                </>
            ),
            key: 'appearance',
            children: <Appearance />,
            // Appearance owns its scrolling region so the action and
            // documentation footers remain fixed within this tab pane.
            style: {
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            },
        },
        {
            label: (
                <>
                    <LucideBarChartBig
                        size='1rem'
                        style={{
                            marginRight: '0.375rem',
                            verticalAlign: '-0.175rem',
                        }}
                    />
                    Usage
                </>
            ),
            key: 'usage',
            children: <Usage />,
        },
        {
            label: (
                <>
                    <TbLockAccess
                        size='1rem'
                        style={{
                            marginRight: '0.375rem',
                            verticalAlign: '-0.175rem',
                        }}
                    />
                    Access
                </>
            ),
            key: 'access',
            children: <Access />,
        },
        {
            label: (
                <>
                    <LucideShieldCheck
                        size='1rem'
                        style={{
                            marginRight: '0.375rem',
                            verticalAlign: '-0.175rem',
                        }}
                    />
                    Identity
                </>
            ),
            key: 'identity',
            children: <Identity />,
        },
    ];

    const location = useLocation();

    const activeKey = location.pathname.split('/')[4];

    return (
        <FullHeightFlexColumn>
            <Metadata />
            <style>
                {`
                    .workspace-advanced-tabs {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        min-height: 0;
                    }

                    .workspace-advanced-tabs > .ant-tabs-nav {
                        flex: none;
                        margin-bottom: 0;
                    }

                    .workspace-advanced-tabs > .ant-tabs-content-holder {
                        flex: 1 1 0%;
                        min-height: 0;
                    }

                    .workspace-advanced-tabs > .ant-tabs-content-holder > .ant-tabs-content,
                    .workspace-advanced-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                        height: 100%;
                        min-height: 0;
                    }

                    .workspace-advanced-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                        overflow: auto;
                    }
                `}
            </style>
            <Typography.Title level={1} style={{ flex: 'none' }}>
                Advanced
            </Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary, flex: 'none' }}>
                See and update your advanced workspace settings below.
            </Typography.Paragraph>
            <PlatformAlert
                styles={{ flex: 'none' }}
                message={<>Need Migration Assitance?</>}
                status='info'
                description={
                    <>
                        <div style={{ marginBottom: '0.75rem' }}>
                            Move off of your legacy research infrastructure today!
                        </div>
                        <Button
                            icon={<TbCalendarMonth size='1rem' style={{ verticalAlign: '-0.175rem' }} />}
                            onClick={() =>
                                window.open('https://cal.com/countercyclical/migration-assistance', '_blank')
                            }
                        >
                            Schedule a Demo
                            <TbArrowUpRight size='1rem' style={{ verticalAlign: '-0.175rem' }} />
                        </Button>
                    </>
                }
            />
            <Divider style={{ flex: 'none', marginTop: '1rem', marginBottom: 0 }} />
            <FlexContentRegion>
                <Routes>
                    <Route index element={<Navigate to={`${url}/appearance`} replace />} />
                    <Route
                        path='*'
                        element={
                            <Tabs
                                items={items}
                                animated={true}
                                activeKey={activeKey}
                                className='workspace-advanced-tabs'
                                size='small'
                                onChange={onChangeTab}
                            />
                        }
                    />
                </Routes>
            </FlexContentRegion>
        </FullHeightFlexColumn>
    );
};

export default Advanced;
